import type { Hex, PublicClient, SendTransactionParameters, WalletClient } from 'viem';

import type { AdvancedOrderProvider, AdvancedOrderType } from '@/constants/advancedOrder';

import type { OneInchLimitRawOrder } from './0x.schema';
import { TokenResult } from './0x/token-registry.types';
import type { Price as CodexPrice } from './codex.types';
import type { GaslessApprovalTypes } from './gasless/types';
import type { IntentsPrice, IntentsQuote } from './intents/types';
import { ChainAddress, EthereumAddress, EthereumHash, SolanaAddress } from './models';
import { EIP712TypedData } from './signature';

/**
 * Represents a node in a graph, from a token in the swap route.
 */
export interface Token {
  /** The token address. This is the unique identifier of the token */
  address: string;
  /** The token symbol. This is not guaranteed to be unique, as multiple tokens can have the same symbol */
  symbol: string;
}

/**
 * Represents an edge in a graph, from a fill in the swap route.
 */
export interface Fill {
  /** The contract address of the input token */
  from: string;
  /**  The contract address of the output token */
  to: string;
  /** The liquidity source used in the route */
  source: string;
  /**
   * The percentage of the fill represented as basis points (bps).
   * 1 bps = 0.01%.
   *
   * The proportionBps for each fill step doesn't need to add up to 10000 (100%).
   * Instead, the proportionBps for each fill step represents the relative size
   * of that fill compared to the total trade size.
   *
   * For example, if a fill step has a proportionBps of 5000 (50%), it means that
   * the notional value (size) of that fill is 50% of the total trade size.
   */
  proportionBps: number;
}

/**
 * 0x Route Schema for Sankey Diagram Visualization.
 */
export interface Route {
  /** Details of each segment that 0x routes the swap through. These translate to the edges/links in the sankey diagram. */
  fills: Fill[];
  /** Properties of the tokens involved in the swap. These translate to nodes in the sankey diagram */
  tokens: Token[];
}

/**
 * 0x Token Tax Bps. Swap-related metadata for the buy and sell tokens
 */
export interface TokenTaxBps {
  /** The buy tax in bps of the token. */
  buyTaxBps: string | null;
  /** The sell tax in bps of the token */
  sellTaxBps: string | null;
}

/**
 * 0x Swap-related metadata for the buy and sell token in the swap
 */
export interface TokenMetadata {
  /** The buy tax in bps of the token. */
  buyToken: TokenTaxBps;
  /** The sell tax in bps of the token */
  sellToken: TokenTaxBps;
}

/**
 * 0x Allowance Issue
 */
interface AllowanceIssue {
  /** The `taker`'s current allowance of the spender */
  actual: string;
  /** The address to set the allowance on */
  spender: EthereumAddress;
}

/**
 * 0x Balance Issue
 */
interface BalanceIssue {
  /** The contract address of the `sellToken` */
  token: EthereumAddress;
  /** The current balance of the `sellToken` in the `taker` address */
  actual: string;
  /** The balance of the `sellToken` required for the swap to execute successfully */
  expected: string;
}

/**
 * 0x Issues Schema for price & quote
 */
export interface Issues {
  /** Details of allowances that the `taker` must set for in order to execute the swap successfully. Null if no allowance is required */
  allowance: AllowanceIssue | null;
  /** Details of balance of the `sellToken` that the `taker` must hold. Null if the `taker` has sufficient balance */
  balance: BalanceIssue | null;
  /** List of invalid sources present in included or excluded sources */
  invalidSources: string[];
  /** This is set to `true` when the transaction cannot be validated successfully due to an insufficient balance of the `sellToken`. This transaction is likely to revert if submitted as is. To prevent this issue from being returned, ensure that the `taker` has a sufficient `sellToken` balance. */
  simulationIncomplete: boolean;
  /** Addresses of RWA tokens involved in the trade that are geo-restricted for this user. Null/undefined if no geo restriction applies. Injected by Matcha API routes – not returned by the upstream 0x API. */
  rwaTokens?: string[];
}

/**
 * `swap/allowance-holder/price` object
 */
export interface SwapPrice {
  /** The block number at which the liquidity sources were sampled to generate the quote. This indicates the freshness of the quote */
  blockNumber: string;
  /** The amount of `buyToken` (in `buyToken` units) that will be bought in the swap */
  buyAmount: string;
  /** The contract address of the token to buy in the swap */
  buyToken: EthereumAddress;
  /** Fees to be deducted in this transaction. It contains the `integratorFee`, `zeroExFee` and `gasFee` */
  fees: Fees;
  /** The estimated gas limit that should be used to send the transaction to guarantee settlement */
  gas: string | null;
  /** The gas price (in wei) that should be used to send the transaction. The transaction needs to be sent with this `gasPrice` for it to be successful */
  gasPrice: string;
  /** An object containing potential issues discovered during 0x validation that can prevent the swap from being executed successfully by the `taker` */
  issues: Issues;
  /** The price which must be met or else the entire transaction will revert. This price is influenced by the `slippageBps` parameter. On-chain sources may encounter price movements from quote to settlement */
  minBuyAmount: string;
  /** The path of liquidity sources to be used in executing this swap */
  route: Route;
  /** The amount of `sellToken` (in `sellToken` units) that will be sold in this swap */
  sellAmount: string;
  /** The contract address of the token to sell in the swap */
  sellToken: EthereumAddress;
  /** The estimated total network cost of the swap. On chains where they is no L1 data cost, it is calculated as `gas` * `gasPrice`. On chains where there is an L1 data cost, it is calculated as `gas` * `gasPrice` + L1 data */
  totalNetworkFee: string;
  /** Swap-related metadata for the buy and sell token in the swap */
  tokenMetadata: TokenMetadata;
  /**
   * The slippage (bps) governing this quote: echoed client-side from the request's
   * `slippageBps` (absent = server/relayer default); a server-resolved value (Model B)
   * overrides the echo when the API starts returning one.
   */
  appliedSlippageBps?: number;
  /** Mode behind the sent value; echoed client-side alongside `appliedSlippageBps`. */
  appliedSlippageMode?: 'auto' | 'manual';
}

/**
 * `gasless/price` endpoint object
 */
export interface GaslessPrice {
  /** The block number at which the liquidity sources were sampled to generate the quote. This indicates the freshness of the quote */
  blockNumber: string;
  /** The amount of `buyToken` (in `buyToken` units) that will be bought in the swap */
  buyAmount: string;
  /** The contract address of the token to buy in the swap */
  buyToken: EthereumAddress;
  /** Fees to be deducted in this transaction. It contains the `integratorFee`, `zeroExFee` and `gasFee` */
  fees: Fees;
  /** An object containing potential issues discovered during 0x validation that can prevent the swap from being executed successfully by the `taker` */
  issues: Issues;
  /** The price which must be met or else the entire transaction will revert. This price is influenced by the `slippageBps` parameter. On-chain sources may encounter price movements from quote to settlement */
  minBuyAmount: string;
  /** The path of liquidity sources to be used in executing this swap */
  route: Route;
  /** The amount of `sellToken` (in `sellToken` units) that will be sold in this swap */
  sellAmount: string;
  /** The contract address of the token to sell in the swap */
  sellToken: EthereumAddress;
  /** The address of the target contract that the transaction will be submitted to */
  target: EthereumAddress;
  /** Swap-related metadata for the buy and sell token in the swap */
  tokenMetadata: TokenMetadata;
}

/**
 * `swap/allowance-holder/quote` endpoint object
 */
export interface SwapQuote {
  /** The block number at which the liquidity sources were sampled to generate the quote. This indicates the freshness of the quote */
  blockNumber: string;
  /** The amount of `buyToken` (in `buyToken` units) that will be bought in the swap */
  buyAmount: string;
  /** The contract address of the token to buy in the swap */
  buyToken: EthereumAddress;
  /** Fees to be deducted in this transaction. It contains the `integratorFee`, `zeroExFee` and `gasFee` */
  fees: Fees;
  /** An object containing potential issues discovered during 0x validation that can prevent the swap from being executed successfully by the `taker` */
  issues: Issues;
  /** The price which must be met or else the entire transaction will revert. This price is influenced by the `slippageBps` parameter. On-chain sources may encounter price movements from quote to settlement */
  minBuyAmount: string;
  /** The path of liquidity sources to be used in executing this swap */
  route: Route;
  /** The amount of `sellToken` (in `sellToken` units) that will be sold in this swap */
  sellAmount: string;
  /** The contract address of the token to sell in the swap */
  sellToken: EthereumAddress;
  /** The estimated total network cost of the swap. On chains where they is no L1 data cost, it is calculated as `gas` * `gasPrice`. On chains where there is an L1 data cost, it is calculated as `gas` * `gasPrice` + L1 data */
  totalNetworkFee: string;
  /**  This object contains the details required to submit the transaction */
  transaction: Transaction;
  /** Swap-related metadata for the buy and sell token in the swap */
  tokenMetadata: TokenMetadata;
  /**
   * The slippage (bps) governing this quote: echoed client-side from the request's
   * `slippageBps` (absent = server/relayer default); a server-resolved value (Model B)
   * overrides the echo when the API starts returning one.
   */
  appliedSlippageBps?: number;
  /** Mode behind the sent value; echoed client-side alongside `appliedSlippageBps`. */
  appliedSlippageMode?: 'auto' | 'manual';
  /** The unique ZeroEx identifier of the request */
  zid: string;
}

/**
 * Contains the necessary information to process a gasless approval
 */
interface GaslessApproval {
  /** Enum representing the type of approval */
  type: GaslessApprovalTypes;
  /** The hash for the approval according to EIP-712. If you compute the hash from eip712 field, it should match the value of this field */
  hash: EthereumHash;
  /**  The necessary data for EIP-712. Please don't assume static shapes for `approval.eip712.types`, `approval.eip712.domain`, `approval.eip712.primaryType` and `approval.eip712.message` as they will change depending on the type */
  eip712: EIP712TypedData;
}

/**
 * Gasless trade
 */
interface Trade {
  /** The transaction type determined by the trade route. This is currently just `settler_metatransaction` and could expand to more types in the future */
  type: 'settler_metatransaction';
  /** The hash for the trade according to EIP-712. If you compute the hash from eip712 field, it should match the value of this field */
  hash: EthereumHash;
  /** This is the necessary data for EIP-712. Please don't assume static shapes for `trade.eip712.types`, `trade.eip712.domain`, `trade.eip712.primaryType` and `trade.eip712.message` as they will change depending on the type */
  eip712: EIP712TypedData;
}

/**
 * This object contains the details required to submit the transaction to the chain
 */
export interface Transaction {
  /** The address of the target contract to send calldata (`data`) to */
  to: EthereumAddress;
  /** The calldata containing transaction execution details to be sent to the `to` address */
  data: Hex;
  /** The estimated gas limit that should be used to send the transaction to guarantee settlement */
  gas: string | null;
  /** The gas price (in wei) that should be used to send the transaction */
  gasPrice: string;
  /** The amount of ether (in wei) that should be sent with the transaction */
  value: string;
}

/**
 * Fee information
 */
export interface Fee {
  /** Total amount of the fee */
  amount: string;
  /** Token address which the fee is taken in */
  token: ChainAddress;
  /** Type of the fee (either `volume` or `gas`) */
  type: 'volume' | 'gas';
}

/**
 * Fees object
 */
export interface Fees {
  /** The specified fee to charge and deliver to the `swapFeeRecipient` */
  integratorFee: Fee | null;
  /** Every charged integrator fee, in request order; 0x omits 0 bps entries */
  integratorFees?: Fee[] | null;
  /**  The fee charged by 0x for the trade */
  zeroExFee: Fee | null;
  /**  The gas fee to be used in submitting the transaction */
  gasFee: Fee | null;
}

/**
 * Legacy `gasless/quote` endpoint object (lane retired in favor of intents);
 * still part of the `Quote` union.
 */
interface GaslessQuote {
  /** This is the “approval” object which contains the necessary information to process a gasless approval */
  approval: GaslessApproval | null;
  /** The block number at which the liquidity sources were sampled to generate the quote. This indicates the freshness of the quote */
  blockNumber: string;
  /** The amount of `buyToken` (in `buyToken` units) that will be bought in the swap */
  buyAmount: string;
  /** The contract address of the token to buy in the swap */
  buyToken: EthereumAddress;
  /** Fees to be deducted in this transaction. It contains the `integratorFee`, `zeroExFee` and `gasFee` */
  fees: Fees;
  /** An object containing potential issues discovered during 0x validation that can prevent the swap from being executed successfully by the `taker` */
  issues: Issues;
  /** The price which must be met or else the entire transaction will revert. This price is influenced by the `slippageBps` parameter. On-chain sources may encounter price movements from quote to settlement */
  minBuyAmount: string;
  /** The path of liquidity sources to be used in executing this swap */
  route: Route;
  /** The amount of `sellToken` (in `sellToken` units) that will be sold in this swap */
  sellAmount: string;
  /** The contract address of the token to sell in the swap */
  sellToken: EthereumAddress;
  /** The address of the target contract that the transaction will be submitted to */
  target: EthereumAddress;
  /** Trade object which contains the necessary information to process a gasless trade */
  trade: Trade;
  /** Swap-related metadata for the buy and sell token in the swap */
  tokenMetadata: TokenMetadata;
  /** The unique ZeroEx identifier of the request */
  zid: string;
}

/** Participant in the Solana provider race (Jupiter or 0x). */
export type SolanaSwapProvider = 'jupiter' | 'zeroex';

/**
 * Solana Swap Quote (adapted fromJupiter Swap api)
 */
export interface SolanaSwapQuote {
  /** The block number at which the liquidity sources were sampled to generate the quote. This indicates the freshness of the quote */
  blockNumber: string;
  /** The amount of `sellToken` (in `sellToken` units) that will be sold in this swap */
  sellAmount: string;
  /** The contract address of the token to sell in the swap */
  sellToken: SolanaAddress;
  /** The amount of `buyToken` (in `buyToken` units) that will be bought in the swap */
  buyAmount: string;
  /** The contract address of the token to buy in the swap */
  buyToken: SolanaAddress;
  /** Fees to be deducted in this transaction */
  fees: Fees;
  /** An object containing potential issues discovered during 0x validation that can prevent the swap from being executed successfully by the `taker` */
  issues: Issues;
  /** The estimated total network cost of the swap in lamports */
  totalNetworkFee: string;
  /** Swap-related metadata for the buy and sell token in the swap */
  tokenMetadata: TokenMetadata;
  /** The path of liquidity sources to be used in executing this swap */
  route: Route;
  /** The transaction to be submitted to the chain */
  transaction: string;
  /** Whether the slippage is dynamic */
  isDynamicSlippage: boolean;
  /** The maximum slippage percentage of the transaction */
  maxSlippageBps: number;
  /** Slippage sent with the request, echoed client-side onto the parsed quote. */
  appliedSlippageBps?: number;
  /** Mode behind the sent value, echoed alongside `appliedSlippageBps`. */
  appliedSlippageMode?: 'auto' | 'manual';
  /**
   * Which provider won the quote race. Its presence identifies a race response
   * whose route is eligible for Sankey rendering. Absent while the race flag is off.
   */
  provider?: SolanaSwapProvider;
}

export interface AllowanceParams {
  tokenContractAddress: EthereumAddress;
  contractAddressToApprove: EthereumAddress;
  walletAddress: EthereumAddress;
  publicClient: PublicClient;
}

interface CancelLimitOrderParams {
  chainId: number;
  walletClient: WalletClient;
}

export type { AdvancedOrderProvider } from '@/constants/advancedOrder';

export type OneInchLimitOrderBuildResponse = {
  provider: '1inch';
  chainFamily: 'evm';
  typedData: EIP712TypedData;
  orderHash: Hex;
  rawOrder: OneInchLimitRawOrder;
};

export type DefinitiveSvmInstruction = {
  programId: string;
  accounts: Array<{
    pubkey: string;
    isSigner: boolean;
    isWritable: boolean;
  }>;
  data: string;
};

/** Definitive's post-fee output estimate, returned on every Definitive build. */
export type DefinitiveLimitOrderEstimate = {
  /** Buy-token base units Definitive expects to deliver after platform, integrator, and network fees. */
  netBuyAmount: string;
  /** Total estimated fees in USD (platform + integrator + network). */
  estimatedFeesUsd: string;
};

/** Signing artefacts and the server quote-context lifetime shared by signable Definitive builds. */
type DefinitiveSignableFields = DefinitiveLimitOrderEstimate & {
  provider: 'definitive';
  signable: true;
  quoteId: string;
  /** Epoch ms after which the server quote context is gone and the payload can no longer be submitted. */
  expiresAt: number;
};

export type DefinitiveEvmLimitOrderBuildResponse = DefinitiveSignableFields & {
  chainFamily: 'evm';
  approveTx: {
    to: EthereumAddress;
    data: Hex;
  } | null;
  permitTypedData: string | null;
  orderTypedData: string;
};

export type DefinitiveSvmLimitOrderBuildResponse = DefinitiveSignableFields & {
  chainFamily: 'svm';
  ataSetupIxs: DefinitiveSvmInstruction[] | null;
  delegateIx: DefinitiveSvmInstruction | null;
  sponsoredDelegateTx: string | null;
  wrapInstructions: DefinitiveSvmInstruction[] | null;
  orderMessage: string;
  nonce: string;
  deadline: string;
};

/** A wallet-less pricing preview: carries the estimate only, never a signing payload. */
export type DefinitiveLimitOrderPreviewResponse = DefinitiveLimitOrderEstimate & {
  provider: 'definitive';
  chainFamily: 'evm' | 'svm';
  signable: false;
};

export type DefinitiveLimitOrderBuildResponse =
  | DefinitiveEvmLimitOrderBuildResponse
  | DefinitiveSvmLimitOrderBuildResponse;

export type LimitOrderBuildResponse =
  | OneInchLimitOrderBuildResponse
  | DefinitiveLimitOrderBuildResponse
  | DefinitiveLimitOrderPreviewResponse;

export type LimitOrderSubmitResponse =
  | {
      provider: '1inch';
      posted: boolean;
    }
  | {
      provider: 'definitive';
      orderId: string;
    };

export interface CancelOneInchLimitOrderParams extends CancelLimitOrderParams {
  order: OneInchLimitOrderItem;
}

export type CancelDefinitiveLimitOrderParams = {
  order: DefinitiveLimitOrderItem;
  signMessage: (message: string) => Promise<string>;
};

export interface FillOrderArgs {
  transaction: Transaction;
  chainId: number;
  walletClient: WalletClient;
  txOptions?: Partial<SendTransactionParameters>;
  connectorId?: string;
}

export interface SwapSourcesResponse {
  sources: string[];
}

export type Price = SwapPrice | GaslessPrice | SolanaSwapQuote | IntentsPrice;
export type Quote =
  | (SwapQuote & { taker: EthereumAddress })
  | (GaslessQuote & { taker: EthereumAddress })
  | (IntentsQuote & { taker: EthereumAddress })
  | SolanaSwapQuote;

/**
 * Whether the price charges an in-token gas fee. This is a fee-shape check,
 * not a lane check: intents prices match it too, and gasless-family prices
 * with a null gasFee do not.
 */
export function hasInTokenGasFee(price?: CodexPrice | Quote | SwapPrice | GaslessPrice): boolean {
  if (!price) return false;
  return 'fees' in price && !!price.fees.gasFee;
}

export enum GeneralErrorCodes {
  ValidationFailure = 100,
  MalformedJson = 101,
  OrderSubmissionDisabled = 102,
  Throttled = 103,
  NotImplemented = 104,
  TransactionInvalid = 105,
  UnableToSubmitOnBehalfOfTaker = 106,
  InvalidAPIKey = 107,
  ServiceDisabled = 108,
  InsufficientFunds = 109,
  ETHSellingNotSupported = 110,
  GasEstimationFailed = 111,
}

export enum ValidationErrorCodes {
  RequiredField = 1000,
  IncorrectFormat = 1001,
  InvalidAddress = 1002,
  AddressNotSupported = 1003,
  ValueOutOfRange = 1004,
  InvalidSignatureOrHash = 1005,
  UnsupportedOption = 1006,
  InvalidOrder = 1007,
  InternalError = 1008,
  TokenNotSupported = 1009,
  FieldInvalid = 1010,
}

type ErrorCodes = GeneralErrorCodes | ValidationErrorCodes;

export interface ValidationError {
  field: string;
  code: ErrorCodes;
  reason: string;
  validationErrors?: ValidationError[];
}

export interface OrderConfigInfo {
  maker: string;
  makerAmount: bigint;
  makerToken: EthereumAddress;
  takerAmount: bigint;
  takerToken: EthereumAddress;
  expiry: Date;
}

export interface OneInchLimitOrderItem {
  remainingFillableTakerAmount: string;
  expiry: string;
  makerAmount: string;
  makerToken: EthereumAddress;
  takerAmount: string;
  takerToken: EthereumAddress;
  chainId: number;
  createdAt: string;
  orderHash: string;
  makerTokenResult: TokenResult | null;
  takerTokenResult: TokenResult | null;
  provider: '1inch';
  makerTraits: string;
  status: string;
  advancedOrderType?: 'limit';
  /** The maker no longer holds enough of the maker token to fill the order. */
  insufficientBalance: boolean;
}

export type DefinitiveLimitOrderItem = {
  remainingFillableTakerAmount: string;
  expiry: string;
  makerAmount: string;
  makerToken: ChainAddress;
  takerAmount: string;
  /** Definitive's net-of-fees output estimate from when the order was placed; `takerAmount` stays the gross limit terms. */
  estimatedTakerAmount?: string;
  takerToken: ChainAddress;
  chainId: number;
  createdAt: string;
  orderId: string;
  makerTokenResult: TokenResult | null;
  takerTokenResult: TokenResult | null;
  provider: 'definitive';
  status: string;
  advancedOrderType?: AdvancedOrderType;
  triggerCrossPrice?: string;
  /** The maker no longer holds enough of the maker token to fill the order. */
  insufficientBalance: boolean;
};

export type LimitOrderIdentity =
  | Pick<OneInchLimitOrderItem, 'provider' | 'orderHash'>
  | Pick<DefinitiveLimitOrderItem, 'provider' | 'orderId'>;

/**
 * Individual limit order data in limit order fetch response
 */
export type LimitOrderItem = OneInchLimitOrderItem | DefinitiveLimitOrderItem;
/**
 * Response orderbook endpoint when fetching limit orders
 */
export interface LimitOrderResponse {
  total: number;
  page: number;
  perPage: number;
  records: LimitOrderItem[];
  degradedProviders?: AdvancedOrderProvider[];
}
